import { useEffect, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import Box from '@mui/material/Box'
import { loadScript } from '../../../utils/scriptLoader.js'
import Typography from '@mui/material/Typography'
import Link from '@mui/material/Link'
import Container from '@mui/material/Container'
import './Registration.css'

const NoResultsMessage = ({ notStarted, notPublished }) => {
  const { t } = useTranslation('race')
  let message = t('results.unavailable')
  if (notStarted) message = t('results.notStarted')
  else if (notPublished) message = t('results.notPublished')
  return (
    <Box role='alert'>
      <Typography sx={{ mb: 2 }} variant='h6'>
        {message}
      </Typography>
    </Box>
  )
}

const checkResultsAvailable = async raceId => {
  const server = window.location.href.indexOf('my.raceresult.com') >= 0 ? '' : 'https://my.raceresult.com'
  try {
    const res = await fetch(`${server}/${raceId}/results/config?lang=en&sanitize=false`)
    return res.ok
  } catch {
    return false
  }
}

const initializeRaceResults = raceId => {
  return new Promise((resolve, reject) => {
    const container = document.getElementById('divRRPublish')

    if (!container) {
      reject(new Error('Container not found'))
      return
    }

    if (container.dataset.initialized) {
      resolve()
      return
    }

    const initWidget = () => {
      if (typeof window.RRPublish === 'function') {
        return new window.RRPublish(container, raceId, 'results')
          .then(rrp => {
            rrp.ShowInfoText = false
            container.dataset.initialized = 'true'
            return rrp
          })
      }

      // If RRPublish isn't available, check for initialization function
      if (typeof window.initMRR === 'function') {
        window.initMRR()
        if (typeof window.RRPublish === 'function') {
          return new window.RRPublish(container, raceId, 'results')
            .then(rrp => {
              rrp.ShowInfoText = false
              container.dataset.initialized = 'true'
              return rrp
            })
        }
        return Promise.reject(new Error('RRPublish initialization failed: RRPublish not available after initMRR'))
      }

      // FIX: If neither function is available, force reload the script
      const existingScript = document.querySelectorAll('script[id="mrr-publish"]')[0]
      if (existingScript) {
        existingScript.remove()
      }

      return loadScript('/libs/mrr-publish.js', 'mrr-publish')
        .then(() => {
          // Try again after reload
          if (typeof window.RRPublish === 'function') {
            return new window.RRPublish(container, raceId, 'results')
              .then(rrp => {
                rrp.ShowInfoText = false
                container.dataset.initialized = 'true'
                return rrp
              })
          }
          if (typeof window.initMRR === 'function') {
            window.initMRR()
            return new window.RRPublish(container, raceId, 'results')
              .then(rrp => {
                rrp.ShowInfoText = false
                container.dataset.initialized = 'true'
                return rrp
              })
          }
          throw new Error('RRPublish initialization failed: Functions not available after script reload')
        })
    }

    checkResultsAvailable(raceId)
      .then(available => {
        if (!available) {
          resolve({ unavailable: true })
          return
        }
        return initWidget()
      })
      .then(rrp => {
        if (rrp) resolve(rrp)
      })
      .catch(error => {
        reject(new Error('RRPublish initialization failed: ' + error.message))
      })
  })
}

export default function Results ({ eventDetails, isTabDisabled, today }) {
  const resultsRef = useRef(null)
  const lastInitializedRaceId = useRef(null)
  const { myRaceResultsId } = eventDetails || {}
  const [resultsUnavailable, setResultsUnavailable] = useState(false)

  // Memoize initialization function to prevent recreation on every render
  const initResults = useCallback(() => {
    if (myRaceResultsId && resultsRef.current) {
      initializeRaceResults(myRaceResultsId, resultsRef.current)
        .then(result => setResultsUnavailable(!!result?.unavailable))
        .catch(error => console.error('Results init failed:', error))
    }
  }, [myRaceResultsId])

  // Registration initialization effect
  useEffect(() => {
    if (myRaceResultsId !== lastInitializedRaceId.current &&
        eventDetails.showResults !== false && !isTabDisabled('results')) {
      lastInitializedRaceId.current = myRaceResultsId
      if (resultsRef.current) resultsRef.current.dataset.initialized = ''
      setResultsUnavailable(false)
      initResults()
    }
  }, [myRaceResultsId, eventDetails.status, eventDetails.registrationsClosed, initResults])

  if (isTabDisabled('results') || eventDetails.showResults === false || resultsUnavailable) {
    return (
      <Container maxWidth='xl'>
        <NoResultsMessage
          notStarted={today.isBefore(eventDetails.firstEventDate.startOf('day'))}
          notPublished={resultsUnavailable}
        />
      </Container>
    )
  }

  return (
    <Container maxWidth='xl' sx={{ overflow: 'auto' }} role='main'>
      <Typography sx={{ mb: 2 }}>
        <Trans
          i18nKey='results.embedNote'
          ns='race'
          components={{
            1: <Link
              href={`https://my.raceresult.com/${myRaceResultsId}/results`}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ textDecoration: 'underline' }}
               />
          }}
        />
      </Typography>
      <Box ref={resultsRef} id='divRRPublish' className='RRPublish' />
    </Container>
  )
}

Results.propTypes = {
  eventDetails: PropTypes.shape({
    myRaceResultsId: PropTypes.string,
    firstEventDate: PropTypes.object,
    startTimes: PropTypes.object,
    regCloseHoursBeforeStart: PropTypes.number,
    status: PropTypes.oneOf(['open', 'soon', 'later']),
    registrationsClosed: PropTypes.bool
  })
}
